import React from 'react';
import { useGame } from '../context/GameContext';
import { AvatarChip } from './Avatar';
import { Crown, Heart, Trophy, Skull } from 'lucide-react';

// v2.2 PvP RACE HUD — floating panel top-right showing opponents' floor / hp / score.
// Renders whenever partyState is set and there's more than one member.

export default function PartyRaceHUD() {
  const { partyState, playerName, run } = useGame();
  if (!partyState || !run) return null;
  const members = (partyState.members || []);
  if (members.length < 2) return null; // solo party = no HUD
  const myTag = (playerName || '').toUpperCase();

  // Determine race leader (highest floor, tie-break by score)
  const sorted = [...members].sort((a, b) => (b.floor - a.floor) || (b.score - a.score));
  const leaderTag = sorted[0]?.tag;

  return (
    <div
      className="pvp-race-hud fixed top-2 right-2 z-30 brutal-border bg-[#0a0a0a] p-2 pr-3 flex flex-col gap-2 text-xs"
      data-testid="pvp-race-hud"
      style={{ maxWidth: 320, borderColor: 'var(--pink)', boxShadow: '4px 4px 0 var(--pink)' }}
    >
      <div className="text-[10px] uppercase tracking-widest text-[color:var(--pink)] font-display font-black flex items-center gap-1">
        <Trophy size={12}/> RACE · ROOM {partyState.room_code}
      </div>
      {sorted.map((m) => {
        const isMe = m.tag === myTag;
        const isLeader = m.tag === leaderTag;
        const hpPct = typeof m.hp_pct === 'number' ? m.hp_pct : null;
        const dead = m.status === 'lost' || (hpPct !== null && hpPct <= 0);
        const won = m.status === 'won' || m.boss_defeated;
        return (
          <div
            key={m.tag}
            className="pvp-race-row flex items-center gap-2"
            data-testid={`pvp-race-row-${m.tag}`}
            data-me={isMe ? 'true' : 'false'}
            data-leader={isLeader ? 'true' : 'false'}
            style={{ opacity: dead ? 0.55 : 1 }}
          >
            <AvatarChip avatar={m.avatar} frame={m.avatar?.frame} size={26} testid={`pvp-avatar-${m.tag}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span
                  className="font-display font-black uppercase truncate"
                  style={{ color: isMe ? 'var(--neon)' : 'var(--text)' }}
                  data-testid={`pvp-tag-${m.tag}`}
                >
                  {m.tag}
                </span>
                {isLeader && !dead && <Crown size={12} className="text-[color:var(--pink)] shrink-0"/>}
                {won && <Trophy size={12} className="text-[color:var(--pink)] shrink-0"/>}
                {dead && !won && <Skull size={12} className="text-[color:var(--red)] shrink-0"/>}
              </div>
              <div className="text-[9px] font-mono uppercase text-[color:var(--text-dim)] flex items-center gap-1.5" data-testid={`pvp-stats-${m.tag}`}>
                <span>F<span className="text-[color:var(--neon)]">{m.floor || 0}</span></span>
                {hpPct !== null && (
                  <span className="flex items-center gap-0.5">
                    <Heart size={9} className="text-[color:var(--red)]"/>
                    <span style={{ color: hpPct > 0.5 ? 'var(--neon)' : hpPct > 0.25 ? '#ffd700' : 'var(--red)' }}>
                      {Math.round(hpPct * 100)}%
                    </span>
                  </span>
                )}
                <span>·</span>
                <span className="text-[color:var(--pink)]">{m.score || 0} pts</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
